import React, { useState } from "react";

import { LoadingIcon, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { getOrganizationProject } from "Api/organizations/projects/getOrganizationProject.request";
import { APP_NAME } from "Constants/documentationUrls";
import Button, { ButtonWrapper, AddButton, Link } from "ds/Button";
import Modal from "ds/Modal";
import * as Sticker from "ds/Sticker";
import getUrl from "Libs/getUrl";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import {
  deleteSubscription,
  loadSubscription
} from "Reducers/organization/subscription";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "../styles";
import { CreateProjectButtonProps } from "../types";

const DeleteDemoModal = ({
  isModalOpen,
  setIsModalOpen,
  isLink,
  linkStyle,
  linkVariant = "link",
  openModal,
  label,
  reference
}: CreateProjectButtonProps) => {
  const intl = useIntl();
  const { organizationId: organizationDescriptionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationDescriptionId!
    })
  );

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const project = await getOrganizationProject(
        organization?.id!,
        reference!
      );
      const subscription = await dispatch(
        loadSubscription({
          organizationId: organization?.name!,
          id: project.subscription_id
        })
      ).unwrap();
      await dispatch(
        deleteSubscription({
          subscription,
          organizationId: organization?.name!
        })
      );
      const interval = setInterval(async () => {
        const subscription = await dispatch(
          loadSubscription({
            organizationId: organization?.name!,
            id: project.subscription_id
          })
        );
        if (subscription.type === "app/subscription/rejected") {
          clearInterval(interval);
          navigate(
            getUrl({
              key: "organization.createProject",
              props: { organizationId: organization?.name! }
            })
          );
          setIsLoading(false);
        }
      }, 5000);
    } catch (error) {
      if (typeof error === "string") setError(error);
      if (typeof (error as { title: string }).title === "string")
        setError((error as { title: string }).title);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen!}
        onOpenChange={() => setIsModalOpen?.(!isModalOpen)}
        handleClose={() => setIsModalOpen?.(false)}
        id="delete-demo-project"
      >
        {isLoading ? (
          <S.LoadingContainer>
            <LoadingIcon />
          </S.LoadingContainer>
        ) : error ? (
          <S.ErrorContainer>
            <Sticker.Root priority="critical">
              <Sticker.Icon priority="critical" />
              <Sticker.Text>{error}</Sticker.Text>
            </Sticker.Root>
          </S.ErrorContainer>
        ) : (
          <>
            <Typography.Title tag="h3">
              {intl.formatMessage({
                id: "create_project_button.delete_demo_project.title"
              })}
            </Typography.Title>
            <Typography.BodyText>
              {intl.formatMessage(
                {
                  id: "create_project_button.delete_demo_project.description"
                },
                {
                  link: children => (
                    <Link
                      to={getUrl({
                        key: "organization.subscribe",
                        props: { organizationId: organizationDescriptionId! }
                      })}
                    >
                      {children}
                    </Link>
                  ),
                  br: () => (
                    <>
                      <br />
                      <br />
                    </>
                  ),
                  app_name: APP_NAME
                }
              )}
            </Typography.BodyText>
            <ButtonWrapper justifyContent="end" spacing="modal">
              <Button
                onClick={() => setIsModalOpen?.(false)}
                variant="secondary"
              >
                {intl.formatMessage({
                  id: "cancel"
                })}
              </Button>
              <Button onClick={handleDelete}>
                {intl.formatMessage({
                  id: "create_project_button.delete_demo_project.cta"
                })}
              </Button>
            </ButtonWrapper>
          </>
        )}
      </Modal>
      {isLink ? (
        <Button
          id="create_project"
          variant={linkVariant}
          style={linkStyle}
          onClick={openModal}
          analyticId="create_project"
        >
          {label}
        </Button>
      ) : (
        <AddButton
          id="create_project"
          analyticId="create_project"
          onClick={openModal}
        >
          {label}
        </AddButton>
      )}
    </>
  );
};

export default DeleteDemoModal;
